import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Shield,
  Target,
  Layers,
  Users,
  BookOpen,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Calendar,
  CheckCircle2,
  BarChart3,
  Workflow,
  Lock,
  Database,
} from 'lucide-react';

interface GouvernanceComptesAIOAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId =
  | 'esprit'
  | 'modele'
  | 'comptes'
  | 'botops'
  | 'couts'
  | 'avantapres'
  | 'risques'
  | 'scaling'
  | 'maturite'
  | 'securite'
  | 'raci'
  | 'roadmap'
  | 'rituels';

const tabs: { id: TabId; label: string; icon: typeof Shield }[] = [
  { id: 'esprit', label: 'Esprit & Positionnement', icon: Target },
  { id: 'modele', label: 'Modèle opérationnel', icon: Workflow },
  { id: 'comptes', label: 'Gestion des comptes', icon: Users },
  { id: 'botops', label: 'BotOps & documentation', icon: BookOpen },
  { id: 'couts', label: 'Comparaison des coûts', icon: DollarSign },
  { id: 'avantapres', label: 'Avant / Après', icon: Layers },
  { id: 'risques', label: 'Limites & risques', icon: AlertTriangle },
  { id: 'scaling', label: 'Stratégie & scaling', icon: TrendingUp },
  { id: 'maturite', label: 'Maturité', icon: BarChart3 },
  { id: 'securite', label: 'Sécurité & exposition des données', icon: Database },
  { id: 'raci', label: 'RACI', icon: Users },
  { id: 'roadmap', label: 'Roadmap', icon: Calendar },
  { id: 'rituels', label: 'Gouvernance & rituels', icon: Shield },
];

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
      <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-indigo-300" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/5">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left py-3 px-4 text-indigo-200 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-white/10 hover:bg-white/5">
              {row.map((cell, j) => (
                <td key={j} className="py-3 px-4 text-gray-300 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pill({ children, tone = 'indigo' }: { children: React.ReactNode; tone?: 'indigo' | 'emerald' | 'amber' | 'red' | 'slate' }) {
  const map = {
    indigo: 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30',
    emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
    amber: 'bg-amber-500/15 text-amber-200 border-amber-400/30',
    red: 'bg-red-500/15 text-red-200 border-red-400/30',
    slate: 'bg-slate-500/15 text-slate-200 border-slate-400/30',
  } as const;
  return <span className={`inline-block px-3 py-1 rounded-full border text-xs font-semibold ${map[tone]}`}>{children}</span>;
}

function EspritTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Positionnement directeur" icon={Target}>
        <div className="space-y-3 text-sm text-gray-300">
          <p>
            Cette card formalise un <strong className="text-white">standard PRIsME Master</strong> pour les agents AI opérés sous modèle OAuth.
            Elle ne décrit pas un cas client, ni un workflow TactiCE. Elle fixe un cadre de gouvernance global.
          </p>
          <p>
            Le principe directeur est simple : <strong className="text-white">1 agent = 1 humain = 1 compte OpenAI OAuth</strong>.
            Le but est de réduire la complexité technique, clarifier l'ownership et rendre le coût plus prévisible.
          </p>
        </div>
      </SectionCard>

      <SectionCard title="Pourquoi quitter le modèle multi-provider" icon={Layers}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• OpenRouter, Claude, Gemini et DeepSeek ont permis de tester vite, mais ont introduit une complexité d'arbitrage continue.</li>
          <li>• Le suivi par tokens a déplacé l'attention vers le micro-contrôle des coûts plutôt que vers la valeur produite.</li>
          <li>• La multiplication des fournisseurs augmente la charge mentale, la documentation, les exceptions et les dépendances.</li>
          <li>• Le modèle OAuth recentre l'opération sur un standard plus simple, plus lisible et plus gouvernable.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Principes de gouvernance" icon={Shield}>
        <div className="flex flex-wrap gap-2 mb-4">
          <Pill tone="indigo">Simplicité &gt; optimisation</Pill>
          <Pill tone="emerald">Ownership par humain</Pill>
          <Pill tone="amber">Coût fixe par agent</Pill>
          <Pill tone="slate">Pilotage par productivité</Pill>
        </div>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• La simplicité opérationnelle est considérée comme un actif de gouvernance.</li>
          <li>• Chaque agent doit avoir un propriétaire humain explicite et redevable.</li>
          <li>• Le coût cible devient fixe et anticipable, autour de 200 $ par agent par mois.</li>
          <li>• La performance se mesure d'abord par le ROI, le débit utile et la qualité, non par les tokens consommés.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function ModeleTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Modèle opérationnel cible" icon={Workflow}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• 1 agent = 1 compte.</li>
          <li>• 1 compte = 1 quota.</li>
          <li>• L'exécution se pense d'abord en mode asynchrone.</li>
          <li>• La charge est tirée par backlog et non par logique temps réel continue.</li>
          <li>• Lorsqu'un quota est atteint, l'agent passe en pause contrôlée.</li>
          <li>• L'agent reprend après le reset de la fenêtre 5h, sans changer le cadre d'ownership.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Flow de fonctionnement" icon={Layers}>
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white">
          {['Backlog', 'Agent', 'Execution', 'Quota', 'Pause', 'Resume'].map((step, i, arr) => (
            <div key={step} className="flex items-center gap-2">
              <div className="px-4 py-2 rounded-lg bg-indigo-500/20 border border-indigo-400/30">{step}</div>
              {i < arr.length - 1 && <span className="text-gray-500">→</span>}
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-400 mt-4">
          Ce modèle privilégie la continuité structurée plutôt que la disponibilité permanente. Il convient à un régime d'exécution orchestré par priorités.
        </p>
      </SectionCard>
    </div>
  );
}

function ComptesTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Règles de gouvernance des comptes" icon={Users}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Chaque agent doit disposer de son propre compte OpenAI.</li>
          <li>• Chaque compte doit appartenir à un humain identifié.</li>
          <li>• Les comptes partagés sont interdits dans le modèle cible.</li>
          <li>• Les identifiants, rôles et rattachements doivent être documentés dans BotOps.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Ownership et accountability" icon={Lock}>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">Propriétaire humain</p>
            <p>Porte la légitimité du compte, son rattachement organisationnel et la responsabilité d'usage.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">Plateforme / IT</p>
            <p>Assure la conformité de l'intégration, la sécurité d'accès, et la disponibilité du cadre technique.</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function BotOpsTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="BotOps comme registre central" icon={BookOpen}>
        <p className="text-sm text-gray-300 mb-4">
          BotOps est le registre de référence des bots et agents. Il ne sert pas à exécuter, mais à gouverner l'inventaire, les rattachements et le cycle de vie.
        </p>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• owner</li>
          <li>• account</li>
          <li>• role</li>
          <li>• usage type</li>
          <li>• statut de cycle de vie</li>
        </ul>
      </SectionCard>

      <SectionCard title="Documentation minimale attendue" icon={Shield}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Les credentials doivent être stockés de façon sécurisée.</li>
          <li>• Chaque agent doit avoir une fiche de gouvernance documentée.</li>
          <li>• Les changements de propriétaire, rôle ou statut doivent être tracés.</li>
          <li>• L'entrée et la sortie de service doivent suivre un cycle explicite.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function CoutsTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Comparaison structurée des coûts" icon={DollarSign}>
        <Table
          headers={['Model', 'Cost structure', 'Predictability', 'Complexity', 'Scalability']}
          rows={[
            ['API (OpenRouter/OpenAI)', '~50$/day (~1500$/month)', 'Low', 'Medium', 'Good but variable'],
            ['Multi-provider', 'Variable by provider and routing', 'Low', 'High', 'Harder to govern'],
            ['OAuth (new model)', '~200$/month', 'High', 'Low', 'Horizontal by account'],
          ]}
        />
      </SectionCard>

      <SectionCard title="Lecture exécutive" icon={TrendingUp}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Modèle API : environ 50 $ / jour, soit environ 1 500 $ / mois dans un usage soutenu.</li>
          <li>• Modèle OAuth Pro : environ 200 $ / mois par agent.</li>
          <li>• Économie attendue : environ 80 à 90 % selon le profil d'usage.</li>
          <li>• Le gain principal n'est pas seulement budgétaire, il est aussi administratif et décisionnel.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function AvantApresTab() {
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <SectionCard title="Avant" icon={AlertTriangle}>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Multi providers</li>
            <li>• Token tracking</li>
            <li>• Cost complexity</li>
          </ul>
        </SectionCard>
        <SectionCard title="Après" icon={CheckCircle2}>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Fixed cost</li>
            <li>• Simple ownership</li>
            <li>• No token tracking</li>
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}

function RisquesTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Limites connues" icon={AlertTriangle}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Quota 5h.</li>
          <li>• Quota hebdomadaire.</li>
          <li>• Modèle non conçu pour tous les cas temps réel.</li>
          <li>• Contraintes fair-use à intégrer dans la gouvernance.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Lecture de risque" icon={Shield}>
        <p className="text-sm text-gray-300">
          Le modèle OAuth réduit la complexité d'opération, mais il impose une discipline forte sur le design du backlog, la priorisation et la gestion des pauses de quota.
        </p>
      </SectionCard>
    </div>
  );
}

function ScalingTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Stratégie de scaling" icon={TrendingUp}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Le scaling se fait par multiplication des agents et des comptes.</li>
          <li>• Il s'agit d'un modèle de scaling horizontal.</li>
          <li>• Le modèle cible exclut le multi-agent sur un même compte comme norme d'architecture.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function MaturiteTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Modèle de maturité" icon={BarChart3}>
        <Table
          headers={['Modèle', 'Lisibilité de gouvernance', 'Prévisibilité coût', 'Complexité opérationnelle', 'Maturité']}
          rows={[
            ['API', 'Medium', 'Low', 'Medium', 'Initial'],
            ['Hybrid', 'Medium', 'Medium', 'High', 'Transitional'],
            ['OAuth', 'High', 'High', 'Low', 'Target'],
          ]}
        />
      </SectionCard>
    </div>
  );
}

function SecuriteTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Comparaison des modèles" icon={Database}>
        <Table
          headers={['Model', 'Data exposure risk', 'Control level', 'Traceability', 'Governance level', 'Recommended usage']}
          rows={[
            ['API Token', 'Low when properly governed', 'High', 'High', 'High', 'Sensitive, structured, controlled workloads'],
            ['Business (workspace)', 'Low when properly governed', 'High', 'High', 'Very high', 'Team-wide governed enterprise usage'],
            ['OAuth Personal (current model)', 'Low when properly governed', 'Medium', 'Medium', 'Medium', 'Productivity-first individual agent usage'],
          ]}
        />
        <p className="text-sm text-gray-400 mt-4">
          Message clé: <strong className="text-white">API ≈ Business &gt; OAuth</strong> en gouvernance et en contrôle, mais les trois modèles sont acceptables et sûrs lorsqu'ils sont bien utilisés.
        </p>
      </SectionCard>

      <SectionCard title="Nuance importante" icon={Shield}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Il n'existe pas de différence technique majeure d'exposition des données entre ces modèles dans un usage normal.</li>
          <li>• Les données ne deviennent pas publiques dans aucun de ces cas par défaut.</li>
          <li>• La vraie différence porte surtout sur les garanties contractuelles, les contrôles de gouvernance et les capacités d'audit.</li>
          <li>• <strong className="text-white">OAuth est moins gouverné, pas moins sécurisé par design.</strong></li>
        </ul>
      </SectionCard>

      <SectionCard title="Risques réels" icon={AlertTriangle}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Gestion des credentials, notamment username/password.</li>
          <li>• Mauvais usage humain, par exemple en exposant des données trop sensibles dans les prompts.</li>
          <li>• Moins d'audit centralisé qu'en modèle API ou Business.</li>
          <li>• Compromission d'un compte individuel.</li>
        </ul>
        <p className="text-sm text-gray-400 mt-4">
          Le risque principal est donc surtout <strong className="text-white">interne</strong>, plus qu'un risque OpenAI-side.
        </p>
      </SectionCard>

      <SectionCard title="Règles minimales obligatoires" icon={Lock}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• 1 agent = 1 account, sans partage.</li>
          <li>• 2FA obligatoire sur tous les comptes.</li>
          <li>• Credentials stockés de façon sécurisée dans BotOps, jamais en plain text.</li>
          <li>• Aucune donnée brute hautement sensible dans les prompts.</li>
          <li>• Segmentation explicite entre usage interne et usage client.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Classification des données" icon={Layers}>
        <Table
          headers={['Level', 'Description', 'OAuth Allowed']}
          rows={[
            ['Low', 'Public / non-sensitive', 'YES'],
            ['Medium', 'Internal business data', 'YES (controlled)'],
            ['High', 'Legal, financial, personal sensitive', 'NO → API or Business only'],
          ]}
        />
      </SectionCard>

      <SectionCard title="Positionnement stratégique" icon={Target}>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">OAuth</p>
            <p>Productivity-first model.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">API</p>
            <p>Control-first model.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">Business</p>
            <p>Governance-first model.</p>
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-4">
          Notre choix est clair: nous priorisons la productivité et la simplicité, tout en imposant un socle minimal de gouvernance suffisant pour garder le modèle acceptable et contrôlé.
        </p>
      </SectionCard>
    </div>
  );
}

function RaciTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="RACI de gouvernance" icon={Users}>
        <Table
          headers={['Activité', 'Owner (human)', 'IT / Platform', 'BotOps', 'Direction']}
          rows={[
            ['Ownership du compte', 'A/R', 'C', 'I', 'I'],
            ['Provisioning et intégration', 'I', 'A/R', 'C', 'I'],
            ['Registre BotOps', 'I', 'C', 'A/R', 'I'],
            ['Cadre et arbitrage', 'I', 'C', 'C', 'A/R'],
            ['Audit périodique', 'C', 'R', 'A', 'I'],
          ]}
        />
        <p className="text-xs text-gray-500 mt-3">R = Responsable · A = Accountable · C = Consulté · I = Informé</p>
      </SectionCard>
    </div>
  );
}

function RoadmapTab() {
  return (
    <div className="space-y-4">
      {[
        ['Phase 1', 'Deploy agents'],
        ['Phase 2', 'Structure BotOps'],
        ['Phase 3', 'Stabilize workflows'],
        ['Phase 4', 'Optimize'],
      ].map(([phase, text]) => (
        <div key={phase} className="bg-white/5 rounded-xl p-5 border border-indigo-500/20">
          <div className="text-sm text-indigo-300 font-bold mb-1">{phase}</div>
          <div className="text-white font-semibold">{text}</div>
        </div>
      ))}
    </div>
  );
}

function RituelsTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Rituels de gouvernance" icon={Calendar}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• Weekly usage review.</li>
          <li>• Monthly ROI per agent.</li>
          <li>• Credential audit périodique.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

export default function GouvernanceComptesAIOAuthModal({ isOpen, onClose }: GouvernanceComptesAIOAuthModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');

  const renderTab = () => {
    switch (activeTab) {
      case 'esprit': return <EspritTab />;
      case 'modele': return <ModeleTab />;
      case 'comptes': return <ComptesTab />;
      case 'botops': return <BotOpsTab />;
      case 'couts': return <CoutsTab />;
      case 'avantapres': return <AvantApresTab />;
      case 'risques': return <RisquesTab />;
      case 'scaling': return <ScalingTab />;
      case 'maturite': return <MaturiteTab />;
      case 'securite': return <SecuriteTab />;
      case 'raci': return <RaciTab />;
      case 'roadmap': return <RoadmapTab />;
      case 'rituels': return <RituelsTab />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-7xl max-h-[92vh] overflow-y-auto rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 shadow-2xl"
          >
            <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-indigo-500/20 px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <p className="uppercase tracking-[0.25em] text-indigo-300 text-xs mb-1">PRIsME Master — Contenu en révision</p>
                <h2 className="text-3xl font-bold text-white">Gouvernance des comptes AI & modèle OAuth Agentic (AIKUB)</h2>
                <p className="text-gray-400 mt-1 text-sm max-w-4xl">
                  Standard PRIsME Master pour encadrer le modèle 1 agent = 1 humain = 1 compte OpenAI OAuth, dans une logique de simplicité, ownership clair et coût fixe prévisible.
                </p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 pt-4 pb-2 border-b border-white/10 flex flex-wrap gap-1">
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeTab === id ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {renderTab()}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
